/**
 * useCardState Hook
 * 管理 CardData 狀態和自動計算 CalculationResult
 * Requirements: 1.2, 5.4, 5.5, 5.6, 5.7
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import type { CardData, CalculationResult, CategoryType, DiskConfig, TrafficConfig, ContactInfo } from '../types/card';
import { defaultCardData, defaultCalculationResult } from '../types/card';
import { calculateFinance } from '../utils/calculator';

/**
 * 根據商品類型獲取預設值
 * Property 8: 類型切換欄位一致性
 * Validates: Requirements 1.2, 1.3, 1.4, 1.5
 */
export function getDefaultsForCategory(category: CategoryType): Partial<CardData> {
  const baseDefaults: Partial<CardData> = {
    category,
  };

  switch (category) {
    case 'vps':
      return {
        ...baseDefaults,
        provider: 'DMIT',
        productName: 'PVM.LAX.Pro.WEE',
        region: 'Los Angeles',
        cpu: '1 vCPU AMD EPYC',
        ram: '1 GB',
        disks: [{ size: 10, unit: 'GB', type: 'SSD' }],
        routeType: 'CN2 GIA 優化',
        traffic: {
          isUnlimited: false,
          value: 1000,
          unit: 'GB',
          direction: '雙向計算',
          speed: 1,
          speedUnit: 'Gbps',
          throttleInfo: '',
        },
      };
    case 'vds':
      return {
        ...baseDefaults,
        provider: 'Hetzner',
        productName: 'AX41-NVMe',
        region: 'Germany',
        cpu: 'AMD Ryzen 5 3600',
        ram: '64 GB',
        disks: [{ size: 512, unit: 'GB', type: 'NVMe' }],
        routeType: '國際線路 (BGP)',
        traffic: {
          isUnlimited: true,
          value: 0,
          unit: 'TB',
          direction: '雙向計算',
          speed: 1,
          speedUnit: 'Gbps',
          throttleInfo: '',
        },
      };
    case 'hosting':
      return {
        ...baseDefaults,
        provider: 'SiteGround',
        productName: 'StartUp Plan',
        region: 'Singapore',
        cpu: 'Shared',
        ram: '2 GB',
        disks: [{ size: 10, unit: 'GB', type: 'SSD' }],
        routeType: '國際線路 (BGP)',
        traffic: {
          isUnlimited: true,
          value: 0,
          unit: 'GB',
          direction: '雙向計算',
          speed: 100,
          speedUnit: 'Mbps',
          throttleInfo: '',
        },
      };
    case 'account':
      return {
        ...baseDefaults,
        provider: 'Netflix',
        productName: 'Premium Account',
        region: '',
        cpu: '',
        ram: '',
        disks: [],
        routeType: '',
        traffic: {
          isUnlimited: false,
          value: 0,
          unit: 'GB',
          direction: '雙向計算',
          speed: 0,
          speedUnit: 'Mbps',
          throttleInfo: '',
        },
      };
    case 'domain':
      return {
        ...baseDefaults,
        provider: 'Namecheap',
        productName: 'example.com',
        region: '',
        cpu: '',
        ram: '',
        disks: [],
        routeType: '',
        traffic: {
          isUnlimited: false,
          value: 0,
          unit: 'GB',
          direction: '雙向計算',
          speed: 0,
          speedUnit: 'Mbps',
          throttleInfo: '',
        },
      };
    default:
      return baseDefaults;
  }
}

/**
 * 判斷是否為伺服器類型（需要顯示規格欄位）
 */
export function isServerType(category: CategoryType): boolean {
  return category === 'vps' || category === 'vds' || category === 'hosting';
}

/**
 * useCardState Hook
 * 管理卡片資料狀態和自動計算結果
 */
export function useCardState() {
  const [cardData, setCardData] = useState<CardData>(defaultCardData);
  const [result, setResult] = useState<CalculationResult>(defaultCalculationResult);

  // 當相關欄位變化時自動計算
  useEffect(() => {
    const newResult = calculateFinance(cardData);
    setResult(newResult);
  }, [
    cardData.renewalPrice,
    cardData.billingCycle,
    cardData.nextDueDate,
    cardData.sellingPrice,
    cardData.exchangeRate,
  ]);

  // 類型切換處理 - 重置為該類型的預設值
  const handleCategoryChange = useCallback((category: CategoryType) => {
    const defaults = getDefaultsForCategory(category);
    setCardData((prev) => ({
      ...prev,
      ...defaults,
    }));
  }, []);

  // 更新單一欄位
  const updateField = useCallback(<K extends keyof CardData>(
    field: K,
    value: CardData[K]
  ) => {
    setCardData((prev) => ({ ...prev, [field]: value }));
  }, []);

  // 更新硬碟配置
  const updateDisks = useCallback((disks: DiskConfig[]) => {
    setCardData((prev) => ({ ...prev, disks }));
  }, []);

  // 更新流量配置
  const updateTraffic = useCallback((traffic: TrafficConfig) => {
    setCardData((prev) => ({ ...prev, traffic }));
  }, []);

  // 更新聯繫方式
  const updateContact = useCallback((contact: ContactInfo) => {
    setCardData((prev) => ({ ...prev, contact }));
  }, []);

  // 判斷是否顯示規格欄位
  const showSpecs = useMemo(() => isServerType(cardData.category), [cardData.category]);

  return {
    cardData,
    setCardData,
    result,
    handleCategoryChange,
    updateField,
    updateDisks,
    updateTraffic,
    updateContact,
    showSpecs,
  };
}

export default useCardState;
